import React, { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useController, useFormContext } from 'react-hook-form';
import cn from 'classnames';
import { Url } from '@wix/da-url';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import RadioButton from '@wix/da-ds/pkg/RadioButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  getShippingProfiles,
  getShippingProfilesStatus,
} from '../../../../redux/selectors/shippingProfiles';
import { fetchShippingProfiles } from '../../../../redux/actions/shippingProfiles';
import { ShippingProfile } from '../../../../redux/types';
import { ArtsAndCraftsDeviationFieldName } from '../../types';

import s from './ShippingProfileField.scss';

function formatRateSummary(profile: ShippingProfile): string {
  return profile.rates
    .map(rate => {
      const name = rate.destinationName ?? rate.destinationType ?? '';
      return `${name} $${rate.priceUsd}`;
    })
    .join(' · ');
}

const ShippingProfileField: React.FC<{}> = () => {
  const { t } = useTranslation();
  const id = useId();

  const profiles = useSelector(getShippingProfiles);
  const profilesStatus = useSelector(getShippingProfilesStatus);

  const dispatchFetchProfiles = useAction(fetchShippingProfiles);
  const dispatchPushModal = useAction(pushModal);

  const { control, formState } = useFormContext();
  const { field } = useController({
    name: ArtsAndCraftsDeviationFieldName.SHIPPING_PROFILE,
    control,
  });
  const error = formState.errors[ArtsAndCraftsDeviationFieldName.SHIPPING_PROFILE];

  useEffect(() => {
    if (profilesStatus === 'uninitialized') {
      dispatchFetchProfiles();
    }
  }, [profilesStatus, dispatchFetchProfiles]);

  const handleManageProfiles = () => {
    dispatchPushModal(ModalType.MANAGE_SHIPPING_PROFILES, {
      wrapInModal: false,
    });
  };

  return (
    <div className={s['root']}>
      <div className={s['header']}>
        <span id={`${id}-label`}>
          <InputLabelText isRequired withRequiredText={false}>
            {t('artsAndCraftsDeviationPopup.form.shippingProfile.label')}
          </InputLabelText>
        </span>
        <TextButton
          size="small"
          variant="edit-purple"
          icon={AddIcon}
          onClick={handleManageProfiles}
        >
          {t('artsAndCraftsDeviationPopup.form.shippingProfile.manageProfiles')}
        </TextButton>
      </div>

      {profiles.length === 0 &&
      profilesStatus !== 'uninitialized' &&
      profilesStatus !== 'fetching' ? (
        <div className={s['empty-state']}>
          <img
            src={Url.staticCDNLink('/eclipse/global/svg/il15-1.svg')}
            alt={t('artsAndCraftsDeviationPopup.form.shippingProfile.label')}
          />
          <p className={s['empty-state-text']}>
            {t('artsAndCraftsDeviationPopup.form.shippingProfile.emptyState.text')}
          </p>
          <GhostButton size="medium" variant="main" icon={AddIcon} onClick={handleManageProfiles}>
            {t('artsAndCraftsDeviationPopup.form.shippingProfile.emptyState.cta')}
          </GhostButton>
        </div>
      ) : (
        <div className={s['profiles']} role="radiogroup" aria-labelledby={`${id}-label`}>
          {profiles
            .filter(profile => profile.shippingProfileid !== undefined)
            .map(profile => {
              const profileId = profile.shippingProfileid as number;
              return (
                <div
                  key={profileId}
                  className={cn(s['profile'], field.value === profileId && s['profile-selected'])}
                >
                  <RadioButton
                    id={`${id}-${profileId}`}
                    name={ArtsAndCraftsDeviationFieldName.SHIPPING_PROFILE}
                    value={String(profileId)}
                    checked={field.value === profileId}
                    onChange={() => field.onChange(profileId)}
                  >
                    <div className={s['profile-content']}>
                      <span className={s['profile-label']}>{profile.title}</span>
                      <span className={s['profile-description']}>{formatRateSummary(profile)}</span>
                    </div>
                  </RadioButton>
                </div>
              );
            })}
        </div>
      )}
      {error && <p className={s['error']}>{error.message as string}</p>}
    </div>
  );
};
ShippingProfileField.displayName = 'ShippingProfileField';

export default ShippingProfileField;
